import Alerter from "@/common/alerter"
import { Entities } from "@/constants"
import { DataContext, QueryContext } from "@/contexts"
import { formatDateKeepZone } from "@/helpers/dates"
import CloseIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material"
import React, { Fragment, useContext, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { cancelSession, finishSession, pendReport } from "../configurations/domain"
import { MeetingFormatTitles, SESSION_STATUS, SESSION_STATUS_ACTION, SESSION_STATUS_OPTIONS } from "../constants"

const StyledStatusBox = styled(Box)(() => ({
  width: "200px",
  textWrap: "balance",
  textAlign: "center",
}))

const StyledStatusGrid = styled(Grid)(() => ({
  width: "100%",
  gap: "10px",
  marginTop: "20px",
}))

const StyledStatusCard = styled(Card)(() => ({
  boxShadow: "none",
  border: "2px solid",
  borderColor: "#D9D2FE",
  textTransform: "none",
  cursor: "pointer",
  minHeight: "120px",
  minWidth: "226px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
}))

const getConnection = (connections, connection_id) => {
  return connections?.find(connection => connection.id === connection_id) || {}
}

export const ChangeStatusDialog = ({ show, onClose, session }) => {
  const { schedule } = useContext(DataContext)
  const { getRequest } = useContext(QueryContext)
  const scheduleId = schedule?.id
  const { id: sessionId, connection_id } = session || {}
  const { educatable, meeting_format, connections } = schedule || {}
  const connection = getConnection(connections, connection_id)
  const { max_paid_noshows, fact_paid_noshows } = connection
  const [status, setStatus] = useState("")
  const [cancelReason, setCancelReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const theme = useTheme()
  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (show && session) {
      setStatus(session.status || "")
      setCancelReason("")
      setIsSubmitting(false)
    }
  }, [show, session])

  const handleClose = () => {
    onClose(true)
  }

  const handleReasonChange = event => {
    setCancelReason(event.target.value)
  }

  const handleStatusChange = statusId => {
    if (statusId !== SESSION_STATUS.CANCELLED && statusId !== SESSION_STATUS.CANCELLED_NO_SHOW) {
      setCancelReason("")
    }
    setStatus(statusId)
  }

  const isSaveDisabled = () => {
    // No changes made
    if (status === session?.status && !cancelReason) {
      return true
    }

    // Cancellation requires a reason
    if (status === SESSION_STATUS.CANCELLED && !cancelReason) {
      return true
    }

    return isSubmitting
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!scheduleId || !sessionId) {
      Alerter.error("Missing schedule or session information!")
      return
    }

    setIsSubmitting(true)
    try {
      const statusKey = Object.keys(SESSION_STATUS).find(key => SESSION_STATUS[key] === status)
      const action = SESSION_STATUS_ACTION[statusKey]

      if (!action) {
        throw new Error(`Invalid status transition: ${status}`)
      }

      const data = { status }

      if (status === SESSION_STATUS.CANCELLED || status === SESSION_STATUS.CANCELLED_NO_SHOW) {
        data.cancellation_reason = cancelReason || (status === SESSION_STATUS.CANCELLED_NO_SHOW ? "No show" : undefined)
        // Get request function for cancelSession
        const request = getRequest(cancelSession)
        await request({
          entitiesIds: {
            [Entities.Schedules]: scheduleId,
            [Entities.Sessions]: sessionId,
          },
          data,
        })
      } else if (status === SESSION_STATUS.FINISHED) {
        // Get request function for finishSession
        const request = getRequest(finishSession)
        await request({
          entitiesIds: {
            [Entities.Schedules]: scheduleId,
            [Entities.Sessions]: sessionId,
          },
        })
      } else if (status === SESSION_STATUS.PENDING) {
        // Use pendReport domain function
        const request = getRequest(pendReport)
        await request({
          entitiesIds: {
            [Entities.Schedules]: scheduleId,
            [Entities.Sessions]: sessionId,
          },
        })
      }

      if (isMounted.current) {
        Alerter.success(`Session status successfully updated to ${status}!`)
        handleClose()
      }
    } catch (error) {
      if (isMounted.current) {
        const errors = error.response?.data?.errors || {}
        const [firstKey, firstValue] = Object.entries(errors)[0] || []
        const errorMessage =
          firstKey && firstValue?.[0]
            ? `${firstKey}: ${firstValue[0]}`
            : "Something went wrong while updating session status!"
        Alerter.error(errorMessage)
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false)
      }
    }
  }

  if (!session || !schedule) {
    return null
  }

  return (
    <Dialog
      open={show}
      onClose={!isSubmitting ? onClose : undefined}
      fullWidth
      aria-labelledby="change-session-status-dialog"
      PaperProps={{ elevation: 6 }}
    >
      <Box sx={{ p: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="body1" sx={{ fontSize: "medium" }} id="change-session-status-dialog">
            Change Session Status:
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            sx={{ color: "text.primary" }}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {educatable?.name} on {meeting_format === "online" ? "Web" : MeetingFormatTitles[meeting_format]},{" "}
          {formatDateKeepZone(session.started_at, "MMMM D, h:mma")}
        </Typography>
        <Box>
          <form onSubmit={handleSubmit}>
            <StyledStatusGrid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              {SESSION_STATUS_OPTIONS.map(({ id, title, description }) => (
                <StyledStatusCard
                  key={id}
                  role="button"
                  aria-pressed={id === status}
                  tabIndex={0}
                  sx={{
                    backgroundColor: id === status ? "#D9D2FE" : "white",
                    color: theme.palette.btBlue?.[80] || "primary.main",
                    "&:hover": {
                      backgroundColor: id === status ? "#D9D2FE" : "rgba(217, 210, 254, 0.1)",
                    },
                  }}
                  onClick={() => handleStatusChange(id)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      handleStatusChange(id)
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <StyledStatusBox>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#3c00f5" }}>
                      {title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#3c00f5" }}>
                      {description}
                    </Typography>
                  </StyledStatusBox>
                </StyledStatusCard>
              ))}
            </StyledStatusGrid>

            {status === SESSION_STATUS.FINISHED && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography>
                  By selecting &quot;Finished&quot;, you are confirming that at least one student attended the session.
                  Marking the session as &quot;Finished&quot; does not record student attendance—
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    you must also record student attendance in your weekly report.
                  </Typography>{" "}
                </Typography>
              </Box>
            )}

            {status === SESSION_STATUS.CANCELLED && (
              <Box sx={{ display: "flex", flexDirection: "column", width: "100%", mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Cancellation reason*
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Please note that canceled sessions are not paid.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Type here..."
                  value={cancelReason}
                  onChange={handleReasonChange}
                  variant="outlined"
                  disabled={isSubmitting}
                  required
                  aria-required="true"
                />
              </Box>
            )}

            {status === SESSION_STATUS.CANCELLED_NO_SHOW && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography>
                  <Typography component="span" sx={{ fontWeight: "bold" }}>
                    {fact_paid_noshows || 0} of {max_paid_noshows || 0} paid no-show sessions used.
                  </Typography>{" "}
                  If you have attendance concerns, please contact{" "}
                  <Link href="mailto:tutors@braintrusttutors.com">tutors@braintrusttutors.com</Link>.
                </Typography>
              </Box>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={onClose} disabled={isSubmitting} color="primary">
                Cancel
              </Button>
              <Button variant="contained" type="submit" color="primary" disabled={isSaveDisabled()}>
                {isSubmitting ? (
                  <Fragment>
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                    Saving...
                  </Fragment>
                ) : (
                  "Save"
                )}
              </Button>
            </Stack>
          </form>
        </Box>
      </Box>
    </Dialog>
  )
}
